'use client'

import { dayjs } from '@shared/libs/dayjs'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { getBrowserSystemLocale, persistBrowserLocaleCookies } from './client'
import { DEFAULT_LANGUAGE_CODE, type LanguageCode, type Locale } from './locales'
import { applyLanguagePacks, translate, type Messages, type TranslationValues } from './messages'
import { normalizeLanguageCode, resolveEffectiveLocale } from './resolve'

type LocaleState = {
  languageCode: LanguageCode
  loginCountry: string | null
  systemLocale: string | null
  effectiveLocale: Locale
  setLanguageCode: (languageCode: LanguageCode | string | null | undefined) => void
  setSystemLocale: (systemLocale: string | null | undefined) => void
  t: (key: string, values?: TranslationValues) => string
}

const I18nContext = createContext<LocaleState | null>(null)

export const I18nProvider = ({
  children,
  initialLanguageCode = DEFAULT_LANGUAGE_CODE,
  initialLoginCountry = null,
  initialSystemLocale,
  i18nEnabled = true,
  messages,
}: {
  children: ReactNode
  initialLanguageCode?: LanguageCode
  initialLoginCountry?: string | null
  initialSystemLocale?: string | null
  i18nEnabled?: boolean
  messages?: Record<Locale, Messages>
}) => {
  // ponytail: 서버가 GCS에서 받은 카탈로그를 클라 모듈 전역에 1회 하이드레이트(렌더 중, 자식보다 먼저).
  //           미지정 시 번들 유지. messages 는 서버가 렌더에 쓴 것과 동일 → 하이드레이션 불일치 없음.
  const hydratedRef = useRef(false)
  if (messages && !hydratedRef.current) {
    hydratedRef.current = true
    applyLanguagePacks(messages)
  }

  const [languageCode, setLanguageCodeState] = useState<LanguageCode>(normalizeLanguageCode(initialLanguageCode))
  const [systemLocale, setSystemLocaleState] = useState<string | null>(initialSystemLocale ?? null)

  useEffect(() => {
    setSystemLocaleState(getBrowserSystemLocale())
  }, [])

  const effectiveLocale = useMemo(
    () => resolveEffectiveLocale({ languageCode, loginCountry: initialLoginCountry, systemLocale, i18nEnabled }),
    [languageCode, initialLoginCountry, systemLocale, i18nEnabled],
  )

  useEffect(() => {
    document.documentElement.lang = effectiveLocale
    dayjs.locale(effectiveLocale)
    persistBrowserLocaleCookies({ languageCode, systemLocale, effectiveLocale })
  }, [effectiveLocale, languageCode, systemLocale])

  const setLanguageCode = useCallback((nextLanguageCode: LanguageCode | string | null | undefined) => {
    setLanguageCodeState(normalizeLanguageCode(nextLanguageCode))
  }, [])

  const setSystemLocale = useCallback((nextSystemLocale: string | null | undefined) => {
    setSystemLocaleState(nextSystemLocale ?? null)
  }, [])

  const t = useCallback(
    (key: string, values?: TranslationValues) => translate(effectiveLocale, key, values),
    [effectiveLocale],
  )

  const value = useMemo(
    () => ({
      languageCode,
      loginCountry: initialLoginCountry,
      systemLocale,
      effectiveLocale,
      setLanguageCode,
      setSystemLocale,
      t,
    }),
    [effectiveLocale, initialLoginCountry, languageCode, setLanguageCode, setSystemLocale, systemLocale, t],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider')
  }
  return context
}

export const useTranslation = () => {
  return useI18n().t
}
